import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import { useApp } from "./AppContext";
import { useAuth } from "./AuthContext";
import {
  Notification,
  NotificationContextType,
  SequencedSyncEvent,
  SyncEvent,
} from "../types/notifications";
import {
  SequencedWaitlistSyncEvent,
  WaitlistSyncEvent,
} from "../types/waitlist";
import { SYNC_EVENT_BUFFER_SIZE } from "../utils/syncEventCursor";
import { RELEASE_NOTE_EVENT } from "../utils/releaseNotes";

/** Overlay server copies onto existing items (server fields win). */
function mergeNotifications(
  prev: Notification[],
  byId: Map<string, Notification>,
): Notification[] {
  let changed = false;
  const next = prev.map((existing) => {
    const update = byId.get(existing.notification_id);
    if (!update) return existing;
    changed = true;
    return {
      ...existing,
      ...update,
      body: { ...existing.body, ...update.body },
    };
  });
  return changed ? next : prev;
}

const byCreatedDesc = (a: Notification, b: Notification) =>
  (b.created_dt || "").localeCompare(a.created_dt || "");

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export function NotificationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { getNotifications, updateNotificationReadStatus } = useApp();
  const { isAuthenticated } = useAuth();
  const [notificationsList, setNotificationsList] = useState<Notification[]>(
    [],
  );
  const [historicalNotifications, setHistoricalNotifications] = useState<
    Notification[]
  >([]);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState<number>(0);
  const [isBellRed, setIsBellRed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pusherSyncEvents, setPusherSyncEvents] = useState<
    SequencedSyncEvent[]
  >([]);
  // Monotonic per-session sequence for sync events. Never reset (not even on
  // logout) so a consumer's lastSeenSeq can't collide with a later event.
  const syncSeqRef = useRef(0);
  // waitlist.sync events: same bounded, seq-stamped buffer shape (own seq).
  const [waitlistSyncEvents, setWaitlistSyncEvents] = useState<
    SequencedWaitlistSyncEvent[]
  >([]);
  const waitlistSeqRef = useRef(0);
  // Latest list snapshots for upsertNotifications, which must decide which
  // incoming items are new without being re-created on every list change.
  const liveRef = useRef<Notification[]>(notificationsList);
  liveRef.current = notificationsList;
  const historicalRef = useRef<Notification[]>(historicalNotifications);
  historicalRef.current = historicalNotifications;

  // Calculate unacknowledged count from combined notifications
  useEffect(() => {
    const unreadCount = allNotifications.filter(
      (notification) => !notification.is_read,
    ).length;
    setUnacknowledgedCount(unreadCount);
  }, [allNotifications]);

  useEffect(() => {
    setAllNotifications(
      [...notificationsList, ...historicalNotifications].filter(
        (notification, index, self) =>
          index ===
          self.findIndex(
            (t) => t.notification_id === notification.notification_id,
          ),
      ),
    );
  }, [notificationsList, historicalNotifications]);

  const fetchNotifications = useCallback(
    async (page: number = 1, append: boolean = false): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await getNotifications(page, 20);

        if (response && response.notifications) {
          const newNotifications: Notification[] = response.notifications;

          if (append) {
            setHistoricalNotifications((prev) => [
              ...prev,
              ...newNotifications,
            ]);
          } else {
            setHistoricalNotifications(newNotifications);
          }

          setHasMore(
            response.pagination &&
              response.pagination.page < response.pagination.total_pages,
          );
          setCurrentPage(page);
        }
      } catch (err) {
        // Don't log 404 errors for new users who aren't associated with a salon yet
        // Check the error message to determine if this is a new user issue
        const status = (err as any)?.status;
        const errorMessage = (err as Error)?.message || "";
        const isNewUserError =
          status === 404 &&
          (errorMessage.includes("not associated with a salon") ||
            errorMessage.includes("User not found"));

        if (isNewUserError) {
          // Silently fail for new users - they just need to complete onboarding first
          return;
        }

        // Log all other errors (including 404s for returning users)
        console.error("Failed to fetch notifications:", err);
        setError(
          "Failed to load historical notifications. Please try again later. If this issue persists, please reach out to customer service for further assistance.",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [getNotifications],
  );

  // Fetch initial notifications once the user is authenticated. Without this
  // gate the provider (mounted at the app root) hits the notifications service
  // on unauthenticated routes like /login and gets an auth error back. On
  // logout we clear state so a subsequent user on the same tab never sees the
  // previous user's notifications.
  useEffect(() => {
    if (!isAuthenticated) {
      setNotificationsList([]);
      setHistoricalNotifications([]);
      setPusherSyncEvents([]);
      setWaitlistSyncEvents([]);
      setIsBellRed(false);
      return;
    }
    fetchNotifications(1, false);
  }, [isAuthenticated, fetchNotifications]);

  const loadMoreNotifications = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchNotifications(currentPage + 1, true);
    }
  }, [fetchNotifications, isLoading, hasMore, currentPage]);

  const markNotificationAsRead = useCallback(
    async (notificationId: string): Promise<void> => {
      // Update local state optimistically, then wait for the PUT so callers
      // that must persist the read state before moving on (the release-note
      // toast's Refresh reloads the page) can await it. Never rejects.
      setNotificationsList((prev) =>
        prev.map((notification) =>
          notification.notification_id === notificationId
            ? { ...notification, is_read: true }
            : notification,
        ),
      );
      setHistoricalNotifications((prev) =>
        prev.map((notification) =>
          notification.notification_id === notificationId
            ? { ...notification, is_read: true }
            : notification,
        ),
      );
      try {
        await updateNotificationReadStatus([
          { notification_id: notificationId, read: true },
        ]);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    },
    [updateNotificationReadStatus],
  );

  const markMultipleNotificationsAsRead = useCallback(
    async (notificationIds: string[]): Promise<void> => {
      try {
        const updates = notificationIds.map((id) => ({
          notification_id: id,
          read: true,
        }));
        updateNotificationReadStatus(updates);

        // Update local state optimistically
        setNotificationsList((prev) =>
          prev.map((notification) =>
            notificationIds.includes(notification.notification_id)
              ? { ...notification, is_read: true }
              : notification,
          ),
        );

        setHistoricalNotifications((prev) =>
          prev.map((notification) =>
            notificationIds.includes(notification.notification_id)
              ? { ...notification, is_read: true }
              : notification,
          ),
        );
      } catch (error) {
        console.error("Failed to mark notifications as read:", error);
      }
    },
    [updateNotificationReadStatus],
  );

  const addNotification = useCallback(
    (data: any, eventName: string = "appointment.created") => {
      // Sync events drive cross-client refetch only — they must NOT enter the
      // notifications list or set the bell. We push them onto pusherSyncEvents
      // (a bounded buffer) and consumers track the last `seq` they processed
      // to refetch the affected transactions. The seq is assigned OUTSIDE the
      // state updater so StrictMode's double-invoked updater stays pure.
      if (eventName === "transaction.sync") {
        if (!data) return;
        syncSeqRef.current += 1;
        const event: SequencedSyncEvent = {
          ...(data as SyncEvent),
          seq: syncSeqRef.current,
        };
        setPusherSyncEvents((prev) => [
          ...prev.slice(-(SYNC_EVENT_BUFFER_SIZE - 1)),
          event,
        ]);
        return;
      }
      // Walk-in waitlist changes: refetch trigger only (never the bell).
      if (eventName === "waitlist.sync") {
        waitlistSeqRef.current += 1;
        const event: SequencedWaitlistSyncEvent = {
          ...((data || {}) as WaitlistSyncEvent),
          seq: waitlistSeqRef.current,
        };
        setWaitlistSyncEvents((prev) => [
          ...prev.slice(-(SYNC_EVENT_BUFFER_SIZE - 1)),
          event,
        ]);
        return;
      }
      if (
        eventName === "appointment.created" ||
        eventName === "appointment.updated" ||
        eventName === "post_appointment_form.submitted" ||
        eventName === "package_purchased" ||
        eventName === "gift_card_purchased" ||
        eventName === "gift_card_claimed" ||
        eventName === RELEASE_NOTE_EVENT
      ) {
        if (!data || !data.notification_id) {
          console.warn("Invalid notification data received:", data);
          return;
        }

        let message = data.message;
        let title = data.title;
        let targetObj = data.target_obj;
        let targetId = data.target_id;
        if (eventName === "post_appointment_form.submitted") {
          const first = (data.client_first_name ?? "").toString().trim();
          const last = (data.client_last_name ?? "").toString().trim();
          const full =
            [first, last].filter(Boolean).join(" ").trim() || "A client";
          message = `${full} submitted a post-appointment form`;
          if (!title) title = "Post-appointment form submitted";
          if (!targetObj && data.transaction_uuid) targetObj = "transaction";
          if (!targetId && data.transaction_uuid)
            targetId = data.transaction_uuid;
        }

        const newNotification: Notification = {
          notification_id: data.notification_id,
          channel_name: "appointment-notifications",
          event_name: eventName,
          salon_uuid: data.salon_uuid || "",
          is_read: false,
          body: {
            message,
            title,
            color: data.color,
            event: data.event,
            target_id: targetId,
            target_obj: targetObj,
            // package_purchased carries the client's uuid alongside the
            // transaction target so the notification can deep-link to the
            // client's profile + Packages tab.
            client_id: data.client_id,
            requires_action: data.requires_action,
            // release_note.published: which note, its optional link, and the
            // "this tab is out of date" flag (see ReleaseNoteWatcher).
            ...(eventName === RELEASE_NOTE_EVENT && {
              release_note_id: data.release_note_id,
              link: data.link,
              requires_refresh: data.requires_refresh,
            }),
          },
          created_dt: new Date().toISOString(),
          updated_dt: new Date().toISOString(),
          updated_by: data.updated_by || "",
        };

        setNotificationsList((prev) => {
          // The same notification can arrive on both the salon channel and
          // the per-staff channel while backends roll out; keep one copy.
          if (
            prev.some(
              (n) => n.notification_id === newNotification.notification_id,
            )
          ) {
            return prev;
          }
          const updated = [newNotification, ...prev];
          // Limit to last 50 notifications
          return updated.slice(0, 50);
        });

        setIsBellRed(true);
      }
    },
    [],
  );

  // Merge server-fetched notifications (e.g. the release-note catch-up after a
  // backgrounded mobile tab) into the feed without duplicating ones already
  // delivered live or loaded by the paginated fetch. Server fields win, so the
  // server's created_dt replaces the live item's client-side "now".
  const upsertNotifications = useCallback((list: Notification[]) => {
    const incoming = (Array.isArray(list) ? list : []).filter(
      (n): n is Notification => !!n && !!n.notification_id,
    );
    if (incoming.length === 0) return;
    const byId = new Map(incoming.map((n) => [n.notification_id, n]));

    // Decide what is new against the latest committed snapshot of both lists.
    const knownIds = new Set(
      [...liveRef.current, ...historicalRef.current].map(
        (n) => n.notification_id,
      ),
    );
    const fresh = Array.from(byId.values()).filter(
      (n) => !knownIds.has(n.notification_id),
    );

    setHistoricalNotifications((prev) => mergeNotifications(prev, byId));
    setNotificationsList((prev) => {
      const merged = mergeNotifications(prev, byId);
      const presentIds = new Set(merged.map((n) => n.notification_id));
      const additions = fresh.filter((n) => !presentIds.has(n.notification_id));
      if (additions.length === 0) return merged;
      // Newest first, matching the live feed; same 50-item cap as live adds.
      return [...additions.sort(byCreatedDesc), ...merged].slice(0, 50);
    });
    if (fresh.some((n) => !n.is_read)) {
      setIsBellRed(true);
    }
  }, []);

  const acknowledgeNotification = useCallback(
    (notificationId: string) => {
      markNotificationAsRead(notificationId);
    },
    [markNotificationAsRead],
  );

  const acknowledgeAllNotifications = useCallback(() => {
    const allNotificationIds = allNotifications
      .filter((notification) => !notification.is_read)
      .map((notification) => notification.notification_id);

    if (allNotificationIds.length > 0) {
      markMultipleNotificationsAsRead(allNotificationIds);
    }
  }, [allNotifications, markMultipleNotificationsAsRead]);

  const markNotificationAsUnread = useCallback(
    async (notificationId: string): Promise<void> => {
      try {
        updateNotificationReadStatus([
          { notification_id: notificationId, read: false },
        ]);
        // Update local state optimistically
        setNotificationsList((prev) =>
          prev.map((notification) =>
            notification.notification_id === notificationId
              ? { ...notification, is_read: false }
              : notification,
          ),
        );
        setHistoricalNotifications((prev) =>
          prev.map((notification) =>
            notification.notification_id === notificationId
              ? { ...notification, is_read: false }
              : notification,
          ),
        );
      } catch (error) {
        console.error("Failed to mark notification as unread:", error);
      }
    },
    [updateNotificationReadStatus],
  );

  // [LOOP] churn probe
  useValueChurnProbe("NotificationContext", {
    allNotifications,
    notificationsList,
    historicalNotifications,
    isLoading,
  });

  const value: NotificationContextType = {
    notifications: notificationsList,
    historicalNotifications,
    allNotifications,
    unacknowledgedCount,
    isBellRed,
    isLoading,
    error,
    hasMore,
    currentPage,
    pusherSyncEvents,
    waitlistSyncEvents,
    addNotification,
    acknowledgeNotification,
    acknowledgeAllNotifications,
    fetchNotifications,
    loadMoreNotifications,
    markNotificationAsRead,
    markMultipleNotificationsAsRead,
    markNotificationAsUnread,
    upsertNotifications,
    setIsBellRed,
  };

  return React.createElement(NotificationContext.Provider, { value }, children);
}

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
}
